import { Form, Button, Table, Modal } from "react-bootstrap";
import XButton from "../../../component/button/XButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faCircleXmark,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import useSidebar from "../../../hooks/useSidebar";
import { API_ENDPOINTS } from "../../../constants/api";
import useAxios from "../../../hooks/useAxios";
import {
  copyToClipboard,
  utcToLocalStringWithTime,
} from "../../../utils/helper";
import { toaster } from "../../../utils/toast";
import { RootState } from "../../../redux/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import XDeleteModal from "../../../component/modal/XDeleteModal";
import { setUserData } from "../../../redux/modules/auth/userSlice";

const ApiKeys = () => {
  const [createKeyModalShow, setCreateKeyModelShow] = useState(false);
  const { onShowSidebar } = useSidebar();
  const { fetchData, postData, deleteData } = useAxios();
  const [generatedKey, setGeneratedKey] = useState("");
  const { api_secret_mask, secret_key_created_date, orgLocations, selectedLocationId } = useSelector(
    (state: RootState) => state.user
  );

  const activeLocation = (orgLocations ?? []).find(
    (loc: any) => String(loc.id) === String(selectedLocationId)
  ) || (orgLocations && orgLocations.length > 0 ? orgLocations[0] : null);

  const locationTimezone = activeLocation?.location_timezone || 'UTC';
  const [isHovered, setIsHovered] = useState(false);
  const [showRevokeKeyModal, setShowRevokeKeyModal] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchUserDetail();
  }, []);

  const fetchUserDetail = async () => {
    const response = await fetchData(API_ENDPOINTS.GET_USER_INFO);
    if (response?.success) {
      dispatch(setUserData(response?.data ?? {}));
    }
  };

  const handleCreateKey = async () => {
    const response = await postData(API_ENDPOINTS.API_SECRET_KEY);
    if (response.success) {
      setGeneratedKey(response.data.secretKey);
    } else {
      toaster({ success: false, message: response?.message ?? "Unable to create Api key" });
    }
  };

  const handleRevokeKey = async () => {
    const response = await deleteData(API_ENDPOINTS.REVOKE_API_SECRET_KEY);
    if (response?.success) {
      fetchUserDetail();
      setShowRevokeKeyModal(false);
      toaster({ success: true, message: response.message });
    } else {
      toaster({ success: false, message: response?.message ?? "Unable to revoke Api key" });
    }
  };

  return (
    <div>
      <div className="main-title d-flex align-items-center justify-content-between main-title-block">
        <h2 className="d-flex align-items-center">
          <span
            className="d-lg-none cursor-pointer"
            onClick={() => onShowSidebar()}
          >
            <FontAwesomeIcon icon={faBars} />
          </span>
          Api Keys
        </h2>
        <div className="filter-bar d-flex align-items-center row-item top-block">
          <XButton
            buttonText="Create Api Key"
            className="btn-theme"
            onClick={() => {
              setCreateKeyModelShow(true);
            }}
          />
        </div>
      </div>
      <span>
        <strong>Note:</strong> Your Api keys are listed below. Please note that
        we do not display your Api key again after you generate them. Do not
        share your Api key with others. Store your keys for future reference.{" "}
        <strong>
          If you believe your key has been compromised, regenerate it
          immediately.
        </strong>
      </span>
      <br />
      <br />
      <div className="table-listing all-contacts users-list position-relative">
        <Table responsive>
          <thead>
            <tr>
              <th>Api Key</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {!api_secret_mask && (
              <tr>
                <td colSpan={9}>
                  <p className="text-center no-records">No Api keys found.</p>
                </td>
              </tr>
            )}
            {api_secret_mask && (
              <tr>
                <td>{api_secret_mask}</td>
                <td>
                  {secret_key_created_date &&
                    utcToLocalStringWithTime(secret_key_created_date, locationTimezone)}
                </td>
                <td className="actions cursor-pointer">
                  <span onClick={() => setShowRevokeKeyModal(true)}>
                    <FontAwesomeIcon icon={faCircleXmark} />
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <Modal
        show={createKeyModalShow}
        onHide={() => {
          setGeneratedKey("");
          fetchUserDetail();
          setCreateKeyModelShow(false);
        }}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Form noValidate>
          <Modal.Header closeButton>
            <Modal.Title>Create Api Key</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {generatedKey ? (
              <>
                Please save this Api key somewhere safe and accessible. For
                security reasons,{" "}
                <strong>you won’t be able to view it again</strong> through your
                Patient Campaign account. If you lose this Api key, you’ll need
                to generate a new one.
                <br />
                <br />
                <span
                  style={{ color: "#13294B" }}
                  className="text-tp text-type"
                >
                  {generatedKey}
                  <FontAwesomeIcon
                    icon={faCopy}
                    onClick={() => copyToClipboard(generatedKey)}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className="ms-3"
                    style={{
                      color: isHovered ? "#7bafd3" : "#13294B",
                      cursor: "pointer",
                    }}
                  />
                </span>
              </>
            ) : (
              <h5
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  minHeight: "150px",
                }}
              >
                Are you sure you want to create a new Api key? <br />
                This action will generate a unique key for secure access. If a
                Api key was previously created, it will be replaced by the new
                one.
              </h5>
            )}
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button
              variant="primary"
              type="submit"
              className="btn-theme btn-border"
              onClick={() => {
                setCreateKeyModelShow(false);
                setGeneratedKey("");
              }}
            >
              {generatedKey ? "Close" : "No, Cancel"}
            </Button>
            {!generatedKey && (
              <Button
                variant="primary"
                className="btn-theme"
                onClick={handleCreateKey}
              >
                Yes, Create Key
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>

      <XDeleteModal
        show={showRevokeKeyModal}
        handleClose={() => setShowRevokeKeyModal(false)}
        title="Revoke Api Key"
        body={
          <p>
            Are you sure you want to revoke API key? Once revoked, this key will
            no longer be valid and any services using it will lose access. This
            action cannot be undone.
            <br />
            <br />
            <span style={{ color: "#13294B" }} className="text-tp text-type">
              {api_secret_mask}
            </span>
          </p>
        }
        onConfirm={() => {
          handleRevokeKey();
        }}
        confirmText={"Yes, Revoke"}
        onCancel={() => setShowRevokeKeyModal(false)}
        cancelText={"No, Cancel"}
      />
    </div>
  );
};

export default ApiKeys;
